import * as turf from "@turf/turf";
import EventEmitter from "events";

export default class InstructionList extends EventEmitter {

  constructor(core, t) {
    super();
    this._core = core;
    this._t = t;
    this._instructions = [];
    this.ready = false;

    this._duration = -1;

    this._currentInstructionId = -1;
    this._metersToInstruction = -1;
    this._arrivedAtDestination = false;

    this.setup();
  }

  setup() {
    this._core.geolocation.on("location", (pos, progressKm) => this.onLocation(pos, progressKm));
  }

  clear() {
    this._instructions = [];
    this.ready = false;
    this._duration = -1;
    this._currentInstructionId = -1;
    this._metersToInstruction = -1;
    this._arrivedAtDestination = false;
  }

  destroy() { }

  onLocation(pos, progressKm) {
    if (!this.ready) return;


    const pt = turf.point([pos.coords.longitude, pos.coords.latitude]);
    const progressMeters = progressKm * 1000;
    const metersToDestination = Math.abs((this._route.lengthKm * 1000.0) - progressMeters);

    if (!this._arrivedAtDestination) {


      if (metersToDestination < 10) {
        this._arrivedAtDestination = true;
        this.emit("arrived", metersToDestination);
      }
    }


    const instruction = this.findRelevantInstruction(progressMeters, pt);

    if (!!instruction && instruction.id !== this._currentInstructionId) {
      this._currentInstructionId = instruction.id;
      this.emit("instruction", instruction, this.nextInstruction);
    }

    // find meters left in instruction

    if (!!instruction) {
      const meters = Math.floor(instruction.metersToInstruction(progressMeters) / 25.0) * 25.0;

      if (meters !== this._metersToInstruction) {
        this._metersToInstruction = meters;

        const nextInstruction = this.nextInstruction;
        const timeToDestination = instruction.timeToDestination(progressMeters);
        const safeTimeToDestination = isNaN(timeToDestination) ? this._duration : timeToDestination;


        if (!!nextInstruction) {
          this.emit("metersToInstruction", meters, Math.floor(nextInstruction.metersToInstruction(progressMeters) / 25.0) * 25.0, safeTimeToDestination, Math.floor(metersToDestination / 25.0) * 25.0);
        } else {
          this.emit("metersToInstruction", meters, 9999999, safeTimeToDestination, Math.floor(metersToDestination / 25.0) * 25.0);
        }
      }
    }
  }

  emitStartMessage(longitude, latitude) {
    this.onLocation({
      coords: {
        longitude,
        latitude,
      }
    }, 0);
  }

  get nextInstruction() {
    if (this._currentInstructionId < this._instructions.length - 1) {
      return this._instructions[this._currentInstructionId + 1];
    }

    return null;
  }

  findRelevantInstruction(progressMeters, pos) {

    for (let i = 0; i < this._instructions.length; i++) {

      let instruction = this._instructions[i];

      if (instruction.isRelevant(progressMeters, pos)) {
        return instruction;
      }
    }

    return null;
  }

  setRoute(route) {
    this.clear();

    this._route = route;
    this._duration = route.duration;

    if (route?.legs?.length > 0) {
      this.compile(route.legs[0]);
    }
  }


  cancel() {
    this.clear();
  }

  compile(leg) {
    const steps = leg.steps;
    let prevDistance = 0;
    let durationLeft = this._duration;
    let prevDuration = 0;


    for (let i = 0; i < steps.length; i++) {
      let step = steps[i];
      const { maneuver } = step;

      const location = turf.point(maneuver.location)
      const point = this._route.nearestPointOnLine(location);

      this._instructions.push(new Instruction(i, maneuver, this.findStepName(steps, i), point.properties.location * 1000, prevDistance, prevDuration, durationLeft, step.distance));

      durationLeft -= step.duration;
      prevDistance = step.distance;
      prevDuration = step.duration;
    }

    this.ready = true;
  }


  findStepName(steps, i) {
    let step = steps[i];

    if (!!step.name) return step.name;
    if (!!step.ref) return step.ref;

    if (i === 0) {
      return step.maneuver.instruction;
    }

    for (let j = i + 1; j < steps.length; j++) {
      let sn = steps[j];
      if (!!sn.name) return sn.name;
      if (!!sn.ref) return sn.ref;
    }

    return "";
  }

}



class Instruction {

  constructor(id, maneuver, name, locationMeters, distance, duration, durationToDest, stepDistance) {
    this.id = id;
    this.name = name;
    this.location = maneuver.location
    this.locationMeters = locationMeters;
    this.isRoundabout = maneuver.type === "roundabout" || maneuver.type === "rotary";
    this.beforeMeters = maneuver.type === "roundabout" ? 30 : 8;
    this.afterMeters = distance;
    this.stepDistance = stepDistance;

    this.duration = duration;
    this.durationToDest = durationToDest;

    this.instruction = maneuver.instruction;
    this.type = maneuver.type;
    this.exit = maneuver.exit;
    this.modifier = this.isRoundabout ? "" : maneuver.modifier;
  }


  bearingDelta(b, a) {
    let delta = (b - a + 540) % 360 - 180;
    return delta;
  }

  getTurnModifier(bearing_before, bearing_after) {
    let delta = this.bearingDelta(bearing_before, bearing_after);

    console.log(delta, bearing_before, bearing_after);

    if (delta >= -15 && delta <= 15) return "straight";
    if (delta > 15 && delta <= 45) return "slight right";
    if (delta > 45 && delta <= 135) return "right";
    if (delta > 135) return "sharp right";
    if (delta < -135) return "sharp left";
    if (delta < -45) return "left";
    if (delta < -15) return "slight left";

    return "straight"; // fallback
  }

  isRelevant(distanceMeters, pos) {

    return (
      (distanceMeters >= (this.locationMeters - this.afterMeters)) &&
      (distanceMeters < (this.locationMeters + this.beforeMeters))
    );
  }

  metersToInstruction(distanceMeters) {
    return Math.max(0, this.locationMeters - distanceMeters);
  }

  isInGeometry(pos) {
    if (this.linestring === null) return false;
    const pt = this.linestring.nearestPointOnLine(pos);
    return pt.properties.dist <= 0.0001;
  }

  timeToDestination(distanceMeters) {
    const remainingInStep = Math.max(0, this.locationMeters - distanceMeters);

    // what fraction of this step is that?
    const frac = Math.min(1, remainingInStep / this.afterMeters);

    // remainingTimeThisStep + everything after
    return this.durationToDest + this.duration * frac;
  }


}